import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"
import { detachGhostOnStart } from "../helpers/sortable_ghost"

// How often a deferred teardown re-checks whether a drag elsewhere on the page has ended.
const TEARDOWN_RETRY_MS = 100

export default class extends Controller {
  static targets = [
    "display",
    "form",
    "allowanceErrorBanner",
    "allowanceErrorText",
    "labelInput",
    "placeholderInput",
    "contentInput",
    "optionsList",
    "optionRow",
    "optionLabel",
    "optionValue",
    "optionDefault",
    // Prüfpunkt of a UVV-Prüfvorlage: Vorhandensein and Funktion rated apart
    "splitPresenceFunction",
    // External value field targets
    "externalSource",
    "externalResponsePath",
    "externalValuePath",
    "externalValueFormat",
    "externalAutoFetch",
    "externalEditable",
    "externalContextList",
    "externalContextRow",
    "externalContextKey",
    "externalContextValue",
    // External table block column mapping targets
    "externalColumnsList",
    "externalColumnRow",
    "externalColumnKey",
    "externalColumnLabel",
    "externalColumnPath",
    "externalColumnType",
    "renderInPdf",
    "pdfTranscript",
    "pdfDisplayStyle",
    "pdfMediaPlacement",
    "pdfImageLayout",
    // Dokumentenablage-Uebernahme (image/file/sketch fields)
    "timesheetSourceKey",
    "timesheetSourceCombine",
    "timesheetSourceCombineWrapper",
    "documentSyncEnabled",
    "documentSyncPanel",
    "documentSyncTrigger",
    "documentSyncTarget",
    "documentSyncFolderPath",
    "documentSyncCreateFolder",
    "inputStyle",
    "groupCollapse",
    "displayStyle",
    "employeeFormat",
    "employeeSeparator",
    "multiplyHours",
    "showTotals",
    "showHoursColumn",
    "hoursSummationMode",
    "hoursMode",
    "pdfColor",
    "pdfColorPicker",
    // Date format override targets
    "dateFormatPreset",
    "dateFormatCustom",
    // Time sheets prefilled block targets
    "stateFilter",
    "dateFrom",
    "dateTo",
    "includeSiteTimesheets",
    "includeTaskTimesheets",
    // Partner companies block targets
    "entryMode",
    "partnerCompanyCreation",
    // Vehicles block targets
    "prefillFromTask",
    "fleetCreation",
    "entryLayout",
    "prefillOwnHoursDefault",
    "prefillForeignHoursDefault",
    // Construction workers only target
    "constructionWorkersOnly",
    // Required block targets
    "blockRequired",
    "requiredColumn",
    "visibleColumn",
    // Timesheet field targets
    "fieldType",
    "requiredInput",
    "visibleInput",
    "perEntryInput",
    // Timesheet upload field (image/file/sketch) settings
    "uploadMultipleInput",
    "documentSyncEnabledInput",
    "documentSyncFolderInput",
    "documentSyncCreateFolderInput",
    "checkInChangeableInput",
    "optionsSection",
    // Option source (typed list, employees, sites on reports) and selection bounds
    "optionsSource",
    "employeeOptionsSettings",
    "staticOptionsSettings",
    "siteOptionsSettings",
    "fleetOptionsSettings",
    "freeTextSettings",
    "allowFreeTextInput",
    "employeeTypeIds",
    "excludeSelfInput",
    "minSelectionsInput",
    "maxSelectionsInput",
    // Timesheet allowance targets
    "nameInput",
    "displayNameInput",
    "showFormulaHintInput",
    "shownToEmployeeInput",
    "inputStepMinutesInput",
    "inputType",
    "formulaType",
    "allowanceType",
    "activeInput",
    "wholeDayInput",
    "perEntryInput",
    "skipOptionInput",
    "oncePerDayInput",
    "oncePerDayWinnerInput",
    "oncePerDayStrictInput",
    "oncePerWeekInput",
    "maxPerWeekInput",
    "maxAmountInput",
    "maxAmountScopeInput",
    "maxHoursInput",
    "maxHoursScopeInput",
    "thresholdScopeInput",
    "minimumHoursInput",
    "validUntilInput",
    // Cross-scope mutual exclusion targets
    "mutuallyExclusiveInput",
    "exclusionScopeInput",
    "exclusionWeekGroupingInput",
    // Per-allowance site blacklist
    "excludedSiteIdsInput",
    "excludedEmployeeIdsInput",
    // Location verification gates (Standortprüfung)
    "suppressStartAtHqInput",
    "hqGeofenceMetersInput",
    "hqStartNoGpsInput",
    "requireSitePresenceInput",
    "sitePresenceNoGpsInput",
    "sitePresenceMinDwellInput",
    "sitePresenceMinPingsInput",
    "sitePresenceMinShareInput",
    "rateInput",
    "multiplierInput",
    "fixedAmountInput",
    "percentageInput",
    "expressionInput",
    "salaryTypeNumberInput",
    "secondarySalaryTypeNumberInput",
    "tierSalaryTypeNumberInput",
    "rateTierEnabledInput",
    "rateTierScopeInput",
    "rateTierThresholdInput",
    "rateTierValueInput",
    "tenureSplitEnabledInput",
    "tenureThresholdModeInput",
    "tenureThresholdDaysInput",
    "tenureThresholdMonthsInput",
    "tenureResetAfterGapDaysInput",
    "mealFixedAmountInput",
    "autoCalculatedInput",
    // Time-based split formula targets
    "referenceTimeFromInput",
    "referenceTimeToInput",
    "triggerDaysContainer",
    "triggerDayInput",
    // Day-of-week split: start after the N-th working hour of the day
    "afterDailyHoursInput",
    // Daily time-window restriction (day/holiday splits + flat per-day/entry)
    "timeWindowFromInput",
    "timeWindowToInput",
    // Holiday split: which region the holidays are detected for
    "holidayRegionSourceInput",
    // Mileage allowance targets
    "mileageCalculationModeInput",
    "mileageRangesContainer",
    "mileageRangeInput",
    "seasonalConfigsContainer",
    "mileageDistanceSourceInput",
    "mileageDistanceMethodInput",
    "mileageReferencePointInput",
    "mileageDistanceBasisInput",
    "mileageRoundDistanceInput",
    "mileageRoundTripInput",
    "mileageCapToHighestRangeInput",
    // Distance-based flat allowance targets
    "distanceFixedAmountInput",
    "distanceThresholdInput",
    "distanceMethodInput",
    "distanceOriginInput",
    "originAddressInput",
    "originLatitudeInput",
    "originLongitudeInput",
    "excludedPostalCodesInput",
    // Wöchentliches Fahrgeld targets
    "wochentlichesFahrgeldRateInput",
    // Gate (per-entry custom-field gate) targets
    "gateFieldKeyInput",
    "gateValuesGroup",
    "gateAllowedValuesInput",
    // Export-Darstellung + per-format skip targets
    "exportValueModeInput",
    "addisonSkipExportInput",
    "gdiSkipExportInput",
    // Custom table block targets
    "customColumnsList",
    "customColumnRow",
    "predefinedRowsList",
    "tradeCountsEnabled",
    "tradeCountsPanel",
    "tradeCountsColumn",
    "predefinedRowItem",
    "predefinedRowInput",
    "allowDynamicRows",
    "showRowLabels",
    "showRowTotals",
    "showColumnTotals",
    "rowLabelHeader",
    // Conditional visibility targets (fields + blocks)
    "conditionsList",
    "conditionRow",
    "conditionsMatch",
    // Field validation rule targets (text/number/date)
    "validationSection",
    "validationMinLength",
    "validationMaxLength",
    "validationPatternPreset",
    "validationPatternCustom",
    "validationMinValue",
    "validationMaxValue",
    "validationMinDate",
    "validationMaxDate",
    "validationErrorMessage",
  ]

  static values = {
    errorMessage: { type: String, default: "Error saving. Please try again." },
    labelPlaceholder: { type: String, default: "Bezeichnung" },
    valuePlaceholder: { type: String, default: "Wert (optional)" },
    defaultLabel: { type: String, default: "Standardwert" },
    removeOptionLabel: { type: String, default: "Option entfernen" },
    columnLabelPlaceholder: { type: String, default: "Bezeichnung" },
    columnWidthPlaceholder: { type: String, default: "Breite (%)" },
    columnTypeText: { type: String, default: "Text" },
    columnTypeNumber: { type: String, default: "Zahl" },
    columnTypeCheckbox: { type: String, default: "Checkbox" },
    columnTypeDropdown: { type: String, default: "Auswahl" },
    rowLabelPlaceholder: { type: String, default: "Zeilenbezeichnung" },
    optionLabelPlaceholder: { type: String, default: "Bezeichnung" },
    optionValuePlaceholder: { type: String, default: "Wert (optional)" },
    columnOptionsTitle: { type: String, default: "Optionen" },
    columnOptionsHint: { type: String, default: "Auswahlmöglichkeiten für diese Spalte" },
    addOptionLabel: { type: String, default: "Option hinzufügen" },
    includeInTotalsLabel: { type: String, default: "In Summen einbeziehen" },
    conditionValuePlaceholder: { type: String, default: "Wert" },
    conditionRemoveLabel: { type: String, default: "Bedingung entfernen" },
    conditionSelectFieldLabel: { type: String, default: "Feld wählen…" },
  }

  optionSortables = []

  connect() {
    clearTimeout(this.teardownTimer)
    this.initializeColumnSortable()
    this.initializePredefinedRowsSortable()
    this.initializeOptionsSortables()
  }

  // Stimulus also disconnects (and at once reconnects) a card that is merely moved in the DOM, which
  // the report builder's drag-reorder does to this very card. Sortable#destroy calls _onDrop on the
  // shared drag state and would abort that drag, so teardown is deferred: a reconnect cancels it,
  // and while any drag is running it waits for that drag to end.
  disconnect() {
    this.scheduleTeardown(0)
  }

  scheduleTeardown(delay) {
    clearTimeout(this.teardownTimer)
    this.teardownTimer = setTimeout(() => {
      if (Sortable.active) return this.scheduleTeardown(TEARDOWN_RETRY_MS)

      this.destroySortables()
    }, delay)
  }

  destroySortables() {
    this.columnSortable?.destroy()
    this.rowSortable?.destroy()
    this.optionSortables.forEach((sortable) => sortable.destroy())
    this.columnSortable = this.rowSortable = null
    this.optionSortables = []
  }

  // Shared SortableJS options, mirroring report_builder_controller#initializeSortable.
  // No onEnd handler: reordering the DOM is enough, since the collect* methods read
  // the columns/options/rows in DOM order when the block is saved.
  sortableOptions(handle) {
    return {
      animation: 150,
      handle,
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      forceFallback: true,
      fallbackClass: "sortable-fallback",
      onStart: detachGhostOnStart,
    }
  }

  initializeColumnSortable() {
    const list = this.hasCustomColumnsListTarget && this.customColumnsListTarget
    if (!list || this.columnSortable?.el === list) return

    this.columnSortable?.destroy()
    this.columnSortable = new Sortable(list, this.sortableOptions("[data-column-drag-handle]"))
  }

  initializePredefinedRowsSortable() {
    const list = this.hasPredefinedRowsListTarget && this.predefinedRowsListTarget
    if (!list || this.rowSortable?.el === list) return

    this.rowSortable?.destroy()
    this.rowSortable = new Sortable(list, this.sortableOptions("[data-row-drag-handle]"))
  }

  // Dropdown-option lists are one per column row and can be created dynamically
  // (addCustomColumn), so init each one individually and guard against double-init.
  // A group card contains its child blocks' cards, whose own field-editors own their lists.
  initializeOptionsSortables() {
    this.element.querySelectorAll("[data-column-options-list]").forEach((list) => {
      if (list.closest('[data-controller~="field-editor"]') === this.element) {
        this.initializeOptionsSortable(list)
      }
    })
  }

  initializeOptionsSortable(listEl) {
    if (!listEl || this.optionSortables.some((sortable) => sortable.el === listEl)) return

    this.optionSortables.push(
      new Sortable(listEl, this.sortableOptions("[data-option-drag-handle]")),
    )
  }

  toggleEdit() {
    if (this.hasDisplayTarget && this.hasFormTarget) {
      this.displayTarget.classList.toggle("hidden")
      this.formTarget.classList.toggle("hidden")
    }
  }

  addOption() {
    if (!this.hasOptionsListTarget) return

    const newRow = document.createElement("div")
    newRow.className = "flex items-center space-x-2"
    newRow.setAttribute("data-field-editor-target", "optionRow")

    newRow.innerHTML = `
      <input type="text"
             data-field-editor-target="optionLabel"
             placeholder="${this.labelPlaceholderValue}"
             class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <input type="text"
             data-field-editor-target="optionValue"
             placeholder="${this.valuePlaceholderValue}"
             class="w-32 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <label class="inline-flex items-center" title="${this.defaultLabelValue}">
        <input type="checkbox"
               data-field-editor-target="optionDefault"
               class="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500">
      </label>
      <button type="button"
              class="p-1 text-gray-400 hover:text-red-500"
              data-action="click->field-editor#removeOption"
              title="${this.removeOptionLabelValue}">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `

    this.optionsListTarget.appendChild(newRow)

    // Focus the new label input
    const labelInput = newRow.querySelector('[data-field-editor-target="optionLabel"]')
    if (labelInput) labelInput.focus()
  }

  removeOption(event) {
    const row = event.target.closest('[data-field-editor-target="optionRow"]')
    if (row) {
      row.remove()
    }
  }

  // Dropdown/multi_select: show the typed option list, the employee filter or the site or
  // vehicle hint, whichever belongs to the chosen source (sites/vehicles only on report fields).
  toggleOptionsSource() {
    const source = this.selectedOptionsSource()
    if (this.hasEmployeeOptionsSettingsTarget) {
      this.employeeOptionsSettingsTarget.classList.toggle("hidden", source !== "employees")
    }
    if (this.hasSiteOptionsSettingsTarget) {
      this.siteOptionsSettingsTarget.classList.toggle("hidden", source !== "sites")
    }
    if (this.hasFleetOptionsSettingsTarget) {
      this.fleetOptionsSettingsTarget.classList.toggle("hidden", source !== "fleets")
    }
    if (this.hasStaticOptionsSettingsTarget) {
      this.staticOptionsSettingsTarget.classList.toggle("hidden", source !== "static")
    }
    if (this.hasFreeTextSettingsTarget) {
      this.freeTextSettingsTarget.classList.toggle("hidden", source === "static")
    }
  }

  selectedOptionsSource() {
    return this.optionsSourceTargets.find((input) => input.checked)?.value || "static"
  }

  // Derive an option value from its label when the user leaves the value blank.
  // German umlauts follow the DIN 5007-2 convention (ä->ae, ß->ss) rather than being
  // dropped, so "Dachfenster Groß" yields "dachfenster_gross" and not "dachfenster_gro".
  slugifyOptionValue(label) {
    const slug = label
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_-]/g, "")
      // Collapse and trim separators so dropped characters ("Öl & Ähre") do not
      // leave doubled underscores - matches Ruby's parameterize.
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "")

    // A label of only symbols would slug to "", which is indistinguishable from
    // the blank option - keep the label-derived fallback in that case.
    return slug || label.toLowerCase().replace(/\s+/g, "_")
  }

  collectOptions() {
    if (!this.hasOptionRowTarget) return []

    const options = []
    this.optionRowTargets.forEach((row) => {
      const labelInput = row.querySelector('[data-field-editor-target="optionLabel"]')
      const valueInput = row.querySelector('[data-field-editor-target="optionValue"]')
      const defaultInput = row.querySelector('[data-field-editor-target="optionDefault"]')

      const label = labelInput?.value?.trim()
      if (!label) return // Skip empty options

      const value = valueInput?.value?.trim() || this.slugifyOptionValue(label)
      const isDefault = defaultInput?.checked || false

      options.push({
        label: label,
        value: value,
        default: isDefault,
      })
    })

    return options
  }

  addExternalContext() {
    if (!this.hasExternalContextListTarget) return

    const newRow = document.createElement("div")
    newRow.className = "flex items-center space-x-2"
    newRow.setAttribute("data-field-editor-target", "externalContextRow")
    newRow.innerHTML = `
      <input type="text"
             data-field-editor-target="externalContextKey"
             placeholder="site"
             class="w-40 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <input type="text"
             data-field-editor-target="externalContextValue"
             placeholder="{{site.number}}"
             class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md font-mono focus:ring-green-500 focus:border-green-500">
      <button type="button" class="p-1 text-gray-400 hover:text-red-500" data-action="click->field-editor#removeExternalContext">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `
    this.externalContextListTarget.appendChild(newRow)
    newRow.querySelector('[data-field-editor-target="externalContextKey"]')?.focus()
  }

  removeExternalContext(event) {
    event.target.closest('[data-field-editor-target="externalContextRow"]')?.remove()
  }

  collectExternalContext() {
    if (!this.hasExternalContextRowTarget) return []

    const rows = []
    this.externalContextRowTargets.forEach((row) => {
      const key = row
        .querySelector('[data-field-editor-target="externalContextKey"]')
        ?.value?.trim()
      const value =
        row.querySelector('[data-field-editor-target="externalContextValue"]')?.value ?? ""
      if (key) rows.push({ key: key, value: value })
    })
    return rows
  }

  addExternalColumn() {
    if (!this.hasExternalColumnsListTarget) return

    const index = this.hasExternalColumnRowTarget ? this.externalColumnRowTargets.length : 0
    const newRow = document.createElement("div")
    newRow.className = "flex items-center space-x-2"
    newRow.setAttribute("data-field-editor-target", "externalColumnRow")
    newRow.innerHTML = `
      <input type="text" data-field-editor-target="externalColumnKey" value="col_${index}"
             class="w-24 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <input type="text" data-field-editor-target="externalColumnLabel" placeholder="Spalte"
             class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <input type="text" data-field-editor-target="externalColumnPath" placeholder="name"
             class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md font-mono focus:ring-green-500 focus:border-green-500">
      <select data-field-editor-target="externalColumnType"
              class="w-28 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
        <option value="text">Text</option>
        <option value="number">Zahl</option>
      </select>
      <button type="button" class="p-1 text-gray-400 hover:text-red-500" data-action="click->field-editor#removeExternalColumn">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `
    this.externalColumnsListTarget.appendChild(newRow)
    newRow.querySelector('[data-field-editor-target="externalColumnLabel"]')?.focus()
  }

  removeExternalColumn(event) {
    event.target.closest('[data-field-editor-target="externalColumnRow"]')?.remove()
  }

  collectExternalColumns() {
    if (!this.hasExternalColumnRowTarget) return []

    const columns = []
    this.externalColumnRowTargets.forEach((row) => {
      const key = row.querySelector('[data-field-editor-target="externalColumnKey"]')?.value?.trim()
      const label = row
        .querySelector('[data-field-editor-target="externalColumnLabel"]')
        ?.value?.trim()
      const path = row
        .querySelector('[data-field-editor-target="externalColumnPath"]')
        ?.value?.trim()
      const type =
        row.querySelector('[data-field-editor-target="externalColumnType"]')?.value || "text"
      if (key) columns.push({ key: key, label: label || key, path: path || "", type: type })
    })
    return columns
  }

  addCustomColumn() {
    if (!this.hasCustomColumnsListTarget) return

    const existingRows = this.hasCustomColumnRowTarget ? this.customColumnRowTargets : []
    if (existingRows.length >= 20) return

    // Find max existing col index
    let maxIndex = -1
    existingRows.forEach((row) => {
      const keyInput = row.querySelector('[data-column-field="key"]')
      const match = keyInput?.value?.match(/^col_(\d+)$/)
      if (match) {
        const idx = parseInt(match[1])
        if (idx > maxIndex) maxIndex = idx
      }
    })

    const newKey = `col_${maxIndex + 1}`
    const newRow = document.createElement("div")
    newRow.setAttribute("data-field-editor-target", "customColumnRow")

    newRow.innerHTML = `
      <div class="flex items-center gap-2">
        <button type="button" class="cursor-move text-gray-400 hover:text-gray-600 shrink-0"
                data-column-drag-handle tabindex="-1" aria-hidden="true">
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
          </svg>
        </button>
        <input type="hidden" data-column-field="key" value="${newKey}">
        <input type="text" data-column-field="label" placeholder="${this.columnLabelPlaceholderValue}"
               class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
        <select data-column-field="column_type"
                data-action="change->field-editor#toggleColumnOptions"
                class="w-28 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
          <option value="text">${this.columnTypeTextValue}</option>
          <option value="number">${this.columnTypeNumberValue}</option>
          <option value="checkbox">${this.columnTypeCheckboxValue}</option>
          <option value="dropdown">${this.columnTypeDropdownValue}</option>
        </select>
        <input type="number" data-column-field="width" placeholder="${this.columnWidthPlaceholderValue}"
               class="w-20 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
               min="0" max="100">
        <button type="button" class="p-1 text-gray-400 hover:text-red-500"
                data-action="click->field-editor#removeCustomColumn">
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
      <div data-column-totals-panel class="mt-1 ml-7 hidden">
        <label class="inline-flex items-center gap-2 text-xs text-gray-700">
          <input type="checkbox" data-column-field="include_in_totals" checked
                 class="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500">
          ${this.includeInTotalsLabelValue}
        </label>
      </div>
      <div data-column-options-panel class="mt-2 ml-2 pl-3 border-l-2 border-gray-200 hidden">
        <p class="text-xs font-medium text-gray-700">${this.columnOptionsTitleValue}</p>
        <p class="text-xs text-gray-500 mb-2">${this.columnOptionsHintValue}</p>
        <div data-column-options-list class="space-y-2"></div>
        <button type="button" class="mt-2 inline-flex items-center px-2 py-1 text-xs text-green-700 hover:bg-green-50 rounded-md border border-green-300"
                data-action="click->field-editor#addColumnOption">
          ${this.addOptionLabelValue}
        </button>
      </div>
    `

    this.customColumnsListTarget.appendChild(newRow)
    this.initializeOptionsSortable(newRow.querySelector("[data-column-options-list]"))
    const labelInput = newRow.querySelector('[data-column-field="label"]')
    if (labelInput) labelInput.focus()
  }

  removeCustomColumn(event) {
    const row = event.target.closest('[data-field-editor-target="customColumnRow"]')
    if (row) row.remove()
  }

  toggleColumnOptions(event) {
    const row = event.target.closest('[data-field-editor-target="customColumnRow"]')
    const panel = row?.querySelector("[data-column-options-panel]")
    if (panel) panel.classList.toggle("hidden", event.target.value !== "dropdown")
    const totalsPanel = row?.querySelector("[data-column-totals-panel]")
    if (totalsPanel) totalsPanel.classList.toggle("hidden", event.target.value !== "number")
  }

  addColumnOption(event) {
    const row = event.target.closest('[data-field-editor-target="customColumnRow"]')
    const list = row?.querySelector("[data-column-options-list]")
    if (!list) return

    const newOption = document.createElement("div")
    newOption.className = "flex items-center gap-2"
    newOption.setAttribute("data-column-option-row", "")

    newOption.innerHTML = `
      <button type="button" class="cursor-move text-gray-400 hover:text-gray-600 shrink-0"
              data-option-drag-handle tabindex="-1" aria-hidden="true">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
        </svg>
      </button>
      <input type="text" data-column-option-field="label" placeholder="${this.optionLabelPlaceholderValue}"
             class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <input type="text" data-column-option-field="value" placeholder="${this.optionValuePlaceholderValue}"
             class="w-32 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <button type="button" class="p-1 text-gray-400 hover:text-red-500"
              data-action="click->field-editor#removeColumnOption">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `

    list.appendChild(newOption)
    const labelInput = newOption.querySelector('[data-column-option-field="label"]')
    if (labelInput) labelInput.focus()
  }

  removeColumnOption(event) {
    const optionRow = event.target.closest("[data-column-option-row]")
    if (optionRow) optionRow.remove()
  }

  addPredefinedRow() {
    if (!this.hasPredefinedRowsListTarget) return

    const newItem = document.createElement("div")
    newItem.className = "flex items-center gap-2"
    newItem.setAttribute("data-field-editor-target", "predefinedRowItem")

    newItem.innerHTML = `
      <button type="button" class="cursor-move text-gray-400 hover:text-gray-600 shrink-0"
              data-row-drag-handle tabindex="-1" aria-hidden="true">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
        </svg>
      </button>
      <input type="text" data-field-editor-target="predefinedRowInput" placeholder="${this.rowLabelPlaceholderValue}"
             class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <button type="button" class="p-1 text-gray-400 hover:text-red-500"
              data-action="click->field-editor#removePredefinedRow">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `

    this.predefinedRowsListTarget.appendChild(newItem)
    const input = newItem.querySelector('[data-field-editor-target="predefinedRowInput"]')
    if (input) input.focus()
  }

  // Picking the day's start or end as source switches the rule to the one it is meant
  // for (earliest start, latest end); the option carries it as data-default-combine.
  // The weather has one value a day, so it hides the rule (data-no-combine).
  timesheetSourceChanged(event) {
    const option = event.target.selectedOptions?.[0]
    if (this.hasTimesheetSourceCombineWrapperTarget) {
      this.timesheetSourceCombineWrapperTarget.classList.toggle(
        "hidden",
        option?.dataset.noCombine === "true",
      )
    }
    const rule = option?.dataset.defaultCombine
    if (!rule || !this.hasTimesheetSourceCombineTarget) return

    const combine = this.timesheetSourceCombineTarget
    const select = this.application.getControllerForElementAndIdentifier(combine, "select")
    if (select) {
      select.setSelected(rule)
    } else {
      combine.value = rule
    }
  }

  removePredefinedRow(event) {
    const item = event.target.closest('[data-field-editor-target="predefinedRowItem"]')
    if (item) item.remove()
  }

  collectCustomColumns() {
    if (!this.hasCustomColumnRowTarget) return []

    const columns = []
    this.customColumnRowTargets.forEach((row) => {
      const key = row.querySelector('[data-column-field="key"]')?.value
      const label = row.querySelector('[data-column-field="label"]')?.value?.trim()
      const columnType = row.querySelector('[data-column-field="column_type"]')?.value || "text"
      const width = row.querySelector('[data-column-field="width"]')?.value || ""

      if (!label) return

      const column = { key, label, column_type: columnType, width }
      if (columnType === "dropdown") column.options = this.collectColumnOptions(row)
      if (columnType === "number") {
        column.include_in_totals =
          row.querySelector('[data-column-field="include_in_totals"]')?.checked !== false
      }
      columns.push(column)
    })

    return columns
  }

  // Scoped to a single column row: Stimulus targets are controller-scoped, so using
  // them here would flatten the options of every column into one list.
  collectColumnOptions(row) {
    const options = []
    const taken = new Set()
    row.querySelectorAll("[data-column-option-row]").forEach((optionRow) => {
      const label = optionRow.querySelector('[data-column-option-field="label"]')?.value?.trim()
      if (!label) return

      const seed =
        optionRow.querySelector('[data-column-option-field="value"]')?.value?.trim() ||
        this.slugifyOptionValue(label)
      options.push({ label, value: this.uniqOptionValue(seed, taken) })
    })
    return options
  }

  // Mirrors uniq_option_value in template_extractor_service.rb: distinct labels can slug
  // to the same value ("Öl" and "Oel" both give "oel"), and the value is what the display
  // side matches on, so a collision would make the later option unreachable.
  uniqOptionValue(seed, taken) {
    let candidate = seed
    let counter = 2
    while (taken.has(candidate)) {
      candidate = `${seed}_${counter}`
      counter += 1
    }
    taken.add(candidate)
    return candidate
  }

  collectPredefinedRows() {
    if (!this.hasPredefinedRowInputTarget) return []

    const rows = []
    this.predefinedRowInputTargets.forEach((input) => {
      const label = input.value?.trim()
      if (label) {
        rows.push({ label })
      }
    })

    return rows
  }

  toggleTradeCounts() {
    if (!this.hasTradeCountsPanelTarget) return
    this.tradeCountsPanelTarget.classList.toggle("hidden", !this.tradeCountsEnabledTarget.checked)
  }

  collectTradeCounts() {
    return {
      enabled: this.tradeCountsEnabledTarget.checked,
      column_key: this.hasTradeCountsColumnTarget ? this.tradeCountsColumnTarget.value : "",
    }
  }

  // --- Conditional visibility (shared by fields + blocks) ---

  addCondition() {
    if (!this.hasConditionsListTarget) return

    const template = this.element.querySelector("[data-condition-template]")
    if (!template) return

    const row = template.content.firstElementChild.cloneNode(true)
    this.conditionsListTarget.appendChild(row)

    const fieldSelect = row.querySelector('[data-condition-field="field_key"]')
    if (fieldSelect) fieldSelect.focus()
  }

  removeCondition(event) {
    const row = event.target.closest('[data-field-editor-target="conditionRow"]')
    if (row) row.remove()
  }

  collectConditions() {
    const match = this.hasConditionsMatchTarget ? this.conditionsMatchTarget.value : "all"

    if (!this.hasConditionRowTarget) return { match, rules: [] }

    const rules = []
    this.conditionRowTargets.forEach((row) => {
      const fieldKey = row.querySelector('[data-condition-field="field_key"]')?.value?.trim()
      if (!fieldKey) return // Skip incomplete rows

      const operator = row.querySelector('[data-condition-field="operator"]')?.value || "equals"
      const value = row.querySelector('[data-condition-field="value"]')?.value ?? ""

      rules.push({ field_key: fieldKey, operator, value })
    })

    return { match, rules }
  }

  // --- Field validation rules (text/number/date) ---

  // Show the custom-regex input only when the "custom" preset is picked.
  togglePatternCustom() {
    if (!this.hasValidationPatternPresetTarget || !this.hasValidationPatternCustomTarget) return

    const isCustom = this.validationPatternPresetTarget.value === "custom"
    this.validationPatternCustomTarget.classList.toggle("hidden", !isCustom)
  }

  checkedValue(targets) {
    const checked = targets.find((input) => input.checked)
    return checked ? checked.value : null
  }

  toggleDocumentSync() {
    if (!this.hasDocumentSyncEnabledTarget || !this.hasDocumentSyncPanelTarget) return

    this.documentSyncPanelTarget.classList.toggle("hidden", !this.documentSyncEnabledTarget.checked)
  }

  collectValidations() {
    const v = {}

    const intValue = (target) => {
      if (!target) return null
      const raw = target.value?.trim()
      if (!raw) return null
      const parsed = parseInt(raw, 10)
      return Number.isNaN(parsed) ? null : parsed
    }

    const numberValue = (target) => {
      if (!target) return null
      const raw = target.value?.trim()
      if (!raw) return null
      const parsed = parseFloat(raw)
      return Number.isNaN(parsed) ? null : parsed
    }

    const minLength = intValue(
      this.hasValidationMinLengthTarget ? this.validationMinLengthTarget : null,
    )
    if (minLength !== null) v.min_length = minLength
    const maxLength = intValue(
      this.hasValidationMaxLengthTarget ? this.validationMaxLengthTarget : null,
    )
    if (maxLength !== null) v.max_length = maxLength

    if (this.hasValidationPatternPresetTarget) {
      const preset = this.validationPatternPresetTarget.value
      if (preset === "custom") {
        v.pattern_preset = "custom"
        const custom = this.hasValidationPatternCustomTarget
          ? this.validationPatternCustomTarget.value?.trim()
          : ""
        if (custom) v.pattern = custom
      } else if (preset) {
        v.pattern_preset = preset
      }
    }

    const minValue = numberValue(
      this.hasValidationMinValueTarget ? this.validationMinValueTarget : null,
    )
    if (minValue !== null) v.min_value = minValue
    const maxValue = numberValue(
      this.hasValidationMaxValueTarget ? this.validationMaxValueTarget : null,
    )
    if (maxValue !== null) v.max_value = maxValue

    if (this.hasValidationMinDateTarget && this.validationMinDateTarget.value) {
      v.min_date = this.validationMinDateTarget.value
    }
    if (this.hasValidationMaxDateTarget && this.validationMaxDateTarget.value) {
      v.max_date = this.validationMaxDateTarget.value
    }

    if (this.hasValidationErrorMessageTarget && this.validationErrorMessageTarget.value?.trim()) {
      v.error_message = this.validationErrorMessageTarget.value.trim()
    }

    return v
  }

  // Save via fetch - no form element needed
  save(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = this.formTarget.dataset.updateUrl
    if (!url) {
      console.error("No update URL found")
      return
    }

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    // Build the data object from inputs
    const data = {}

    // Determine if this is a field or block based on URL
    const isBlock = url.includes("custom_report_block")
    const paramKey = isBlock ? "custom_report_block" : "custom_report_field"

    data[paramKey] = {
      label: this.labelInputTarget.value,
    }

    // Only add placeholder for fields (not blocks)
    if (!isBlock && this.hasPlaceholderInputTarget) {
      data[paramKey].placeholder = this.placeholderInputTarget.value
    }

    // Add content for static_text fields
    if (!isBlock && this.hasContentInputTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.content = this.contentInputTarget.value
    }

    // Add options for selection fields (dropdown/multi_select/radio)
    // Note: field_type is never changed on save - it's set when the field is created
    if (!isBlock && this.hasOptionsListTarget) {
      const options = this.collectOptions()

      data[paramKey].settings = {
        options: options,
      }
    }

    // Where the choices come from. The controller merges settings into the stored ones,
    // so the source is sent with every save — "static" is what clears a picker.
    if (!isBlock && this.hasOptionsSourceTarget) {
      const settings = (data[paramKey].settings = data[paramKey].settings || {})
      settings.options_source = this.selectedOptionsSource()
      if (settings.options_source === "employees" && this.hasEmployeeTypeIdsTarget) {
        settings.employee_type_ids = Array.from(this.employeeTypeIdsTarget.selectedOptions).map(
          (option) => option.value,
        )
      }
      // Sent unticked too: the stored settings would otherwise keep a "true".
      if (this.hasAllowFreeTextInputTarget) {
        settings.allow_free_text =
          settings.options_source !== "static" && this.allowFreeTextInputTarget.checked
      }
    }

    // Add external_value settings
    if (!isBlock && this.hasExternalSourceTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.external_source_id = this.externalSourceTarget.value
        ? parseInt(this.externalSourceTarget.value, 10)
        : null
      data[paramKey].settings.response_path = this.hasExternalResponsePathTarget
        ? this.externalResponsePathTarget.value
        : ""
      data[paramKey].settings.value_path = this.hasExternalValuePathTarget
        ? this.externalValuePathTarget.value
        : ""
      data[paramKey].settings.value_format = this.hasExternalValueFormatTarget
        ? this.externalValueFormatTarget.value
        : "text"
      data[paramKey].settings.auto_fetch =
        this.hasExternalAutoFetchTarget && this.externalAutoFetchTarget.checked
      data[paramKey].settings.editable_after_fetch = this.hasExternalEditableTarget
        ? this.externalEditableTarget.checked
        : true
      data[paramKey].settings.context_params = this.collectExternalContext()
    }

    // Add external_table block settings
    if (isBlock && this.hasExternalSourceTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.external_source_id = this.externalSourceTarget.value
        ? parseInt(this.externalSourceTarget.value, 10)
        : null
      data[paramKey].settings.response_path = this.hasExternalResponsePathTarget
        ? this.externalResponsePathTarget.value
        : ""
      data[paramKey].settings.auto_fetch =
        this.hasExternalAutoFetchTarget && this.externalAutoFetchTarget.checked
      data[paramKey].settings.editable_after_fetch = this.hasExternalEditableTarget
        ? this.externalEditableTarget.checked
        : true
      data[paramKey].settings.context_params = this.collectExternalContext()
      data[paramKey].settings.column_mappings = this.collectExternalColumns()
    }

    // Add render_in_pdf setting for all fields and blocks
    if (this.hasRenderInPdfTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.render_in_pdf = this.renderInPdfTarget.checked
    }

    // Audio fields: print the transcript under the recording's filename
    if (this.hasPdfTranscriptTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.pdf_transcript = this.pdfTranscriptTarget.checked
    }

    // Add date format override (blank preset = inherit from the report's global format)
    if (this.hasDateFormatPresetTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.date_format = {
        preset: this.dateFormatPresetTarget.value,
        custom: this.hasDateFormatCustomTarget ? this.dateFormatCustomTarget.value : "",
      }
    }

    // Add pdf_display_style for text fields
    if (this.hasPdfDisplayStyleTarget) {
      const checkedStyle = this.pdfDisplayStyleTargets.find((input) => input.checked)
      if (checkedStyle) {
        data[paramKey].settings = data[paramKey].settings || {}
        data[paramKey].settings.pdf_display_style = checkedStyle.value
      }
    }

    // Add pdf_media_placement for image/file/sketch fields
    if (this.hasPdfMediaPlacementTarget) {
      const checkedPlacement = this.pdfMediaPlacementTargets.find((input) => input.checked)
      if (checkedPlacement) {
        data[paramKey].settings = data[paramKey].settings || {}
        data[paramKey].settings.pdf_media_placement = checkedPlacement.value
      }
    }

    // Add pdf_image_layout for image/file fields
    if (this.hasPdfImageLayoutTarget) {
      const checkedLayout = this.pdfImageLayoutTargets.find((input) => input.checked)
      if (checkedLayout) {
        data[paramKey].settings = data[paramKey].settings || {}
        data[paramKey].settings.pdf_image_layout = checkedLayout.value
      }
    }

    // Take the answer over from a timesheet field (blank key removes the mapping)
    if (this.hasTimesheetSourceKeyTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.timesheet_source = {
        field_key: this.timesheetSourceKeyTarget.value,
        combine: this.hasTimesheetSourceCombineTarget
          ? this.timesheetSourceCombineTarget.value
          : "",
      }
    }

    // Add document_sync settings for image/file/sketch fields
    if (this.hasDocumentSyncEnabledTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.document_sync = {
        enabled: this.documentSyncEnabledTarget.checked,
        trigger: this.checkedValue(this.documentSyncTriggerTargets) || "on_upload",
        target: this.checkedValue(this.documentSyncTargetTargets) || "reportable",
        folder_path: this.hasDocumentSyncFolderPathTarget
          ? this.documentSyncFolderPathTarget.value
          : "",
        create_folder: this.hasDocumentSyncCreateFolderTarget
          ? this.documentSyncCreateFolderTarget.checked
          : true,
      }
    }

    if (this.hasSplitPresenceFunctionTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.split_presence_function = this.splitPresenceFunctionTarget.checked
    }

    // Add input_style for text fields
    if (this.hasInputStyleTarget) {
      const checkedInputStyle = this.inputStyleTargets.find((input) => input.checked)
      if (checkedInputStyle) {
        data[paramKey].settings = data[paramKey].settings || {}
        data[paramKey].settings.input_style = checkedInputStyle.value
      }
    }

    // Add display_style for multi_select fields (dropdown vs. checkbox list)
    if (this.hasDisplayStyleTarget) {
      const checkedDisplayStyle = this.displayStyleTargets.find((input) => input.checked)
      if (checkedDisplayStyle) {
        data[paramKey].settings = data[paramKey].settings || {}
        data[paramKey].settings.display_style = checkedDisplayStyle.value
      }
    }

    // Add employee display settings for activities blocks
    if (this.hasMultiplyHoursTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.multiply_hours = this.multiplyHoursTarget.checked
    }
    if (this.hasEmployeeFormatTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.employee_format = this.employeeFormatTarget.value
    }
    if (this.hasEmployeeSeparatorTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.employee_separator = this.employeeSeparatorTarget.value
    }

    // Add employees block totals settings
    if (this.hasShowTotalsTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.show_totals = this.showTotalsTarget.checked
    }
    if (this.hasShowHoursColumnTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.show_hours_column = this.showHoursColumnTarget.checked
    }
    if (this.hasHoursSummationModeTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.hours_summation_mode = this.hoursSummationModeTarget.value
    }
    if (this.hasHoursModeTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.hours_mode = this.hoursModeTarget.value
    }

    // Add time_sheets_prefilled block settings
    if (this.hasStateFilterTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.state_filter = this.stateFilterTarget.value
    }
    if (this.hasDateFromTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.date_from = this.dateFromTarget.value || null
    }
    if (this.hasDateToTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.date_to = this.dateToTarget.value || null
    }
    if (this.hasIncludeSiteTimesheetsTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.include_site_timesheets = this.includeSiteTimesheetsTarget.checked
    }
    if (this.hasIncludeTaskTimesheetsTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.include_task_timesheets = this.includeTaskTimesheetsTarget.checked
    }

    // Add partner_companies block settings
    if (this.hasEntryModeTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      const checkedMode = this.entryModeTargets.find((input) => input.checked)
      data[paramKey].settings.entry_mode = checkedMode?.value || "detailed"
    }
    if (this.hasPartnerCompanyCreationTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      const checkedCreation = this.partnerCompanyCreationTargets.find((input) => input.checked)
      data[paramKey].settings.partner_company_creation = checkedCreation?.value || "customer"
    }

    // Add vehicles block settings
    if (this.hasFleetCreationTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      const checkedCreation = this.fleetCreationTargets.find((input) => input.checked)
      data[paramKey].settings.fleet_creation = checkedCreation?.value || "fleet"
    }
    if (this.hasEntryLayoutTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      const checkedLayout = this.entryLayoutTargets.find((input) => input.checked)
      data[paramKey].settings.entry_layout = checkedLayout?.value || "cards"
    }
    if (this.hasPrefillFromTaskTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.prefill_from_task = this.prefillFromTaskTarget.checked
    }
    if (this.hasPrefillOwnHoursDefaultTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.prefill_own_hours_default =
        parseFloat(this.prefillOwnHoursDefaultTarget.value) || 0
    }
    if (this.hasPrefillForeignHoursDefaultTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.prefill_foreign_hours_default =
        parseFloat(this.prefillForeignHoursDefaultTarget.value) || 0
    }

    // Add construction workers only setting
    if (this.hasConstructionWorkersOnlyTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.construction_workers_only = this.constructionWorkersOnlyTarget.checked
    }

    // Add required block settings
    if (this.hasBlockRequiredTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.required = this.blockRequiredTarget.checked
    }
    // Visible columns. The leading "" survives deep_munge, which turns an empty
    // array into nil - the controller would then read "no override" and silently
    // restore every default column. The server compacts the blank away.
    let visibleColumns = null
    if (this.hasVisibleColumnTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      visibleColumns = this.visibleColumnTargets
        .filter((input) => input.checked)
        .map((input) => input.value)
      data[paramKey].settings.columns = ["", ...visibleColumns]
    }
    if (this.hasRequiredColumnTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      // A disabled box is required by the block itself (it books timesheets) — the
      // server adds it, so it is not stored as the admin's own choice.
      data[paramKey].settings.required_columns = this.requiredColumnTargets
        .filter((input) => input.checked && !input.disabled)
        .map((input) => input.value)
        .filter((col) => visibleColumns === null || visibleColumns.includes(col))
    }

    // Add PDF block color setting
    if (this.hasPdfColorTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.pdf_color = this.pdfColorTarget.value || null
    }

    // Add custom table settings
    if (this.hasCustomColumnsListTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.custom_columns = this.collectCustomColumns()
    }
    if (this.hasPredefinedRowsListTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.predefined_rows = this.collectPredefinedRows()
    }
    // "Anzahl je Gewerk aus Stundenzetteln": on/off and the count column
    if (this.hasTradeCountsEnabledTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.timesheet_trade_counts = this.collectTradeCounts()
    }
    if (this.hasAllowDynamicRowsTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.allow_dynamic_rows = this.allowDynamicRowsTarget.checked
    }
    if (this.hasShowRowLabelsTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.show_row_labels = this.showRowLabelsTarget.checked
    }
    if (this.hasShowRowTotalsTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.show_row_totals = this.showRowTotalsTarget.checked
    }
    if (this.hasShowColumnTotalsTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.show_column_totals = this.showColumnTotalsTarget.checked
    }
    if (this.hasRowLabelHeaderTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.row_label_header = this.rowLabelHeaderTarget.value || null
    }

    // Conditional visibility (always send when the UI is present so clearing works
    // against the controller's shallow settings.merge)
    if (this.hasConditionsListTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      const conditions = this.collectConditions()
      data[paramKey].settings.conditions = conditions.rules
      data[paramKey].settings.conditions_match = conditions.match
    }

    // Field validation rules (always sent so clearing persists against the
    // controller's shallow settings.merge)
    if (this.hasValidationSectionTarget) {
      data[paramKey].settings = data[paramKey].settings || {}
      data[paramKey].settings.validations = this.collectValidations()
    }

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error("Update failed")
        }
      })
      .then((html) => {
        // Process Turbo Stream response to replace the card
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        console.error("Error updating:", error)
        alert(this.errorMessageValue)
      })
  }

  // Save timesheet template field via fetch
  saveTimesheetField(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = this.formTarget.dataset.updateUrl
    if (!url) {
      console.error("No update URL found")
      return
    }

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    // Build the data object
    const data = {
      timesheet_template_field: {
        label: this.labelInputTarget.value,
        field_type: this.hasFieldTypeTarget ? this.fieldTypeTarget.value : undefined,
        required: this.hasRequiredInputTarget ? this.requiredInputTarget.checked : undefined,
        visible: this.hasVisibleInputTarget ? this.visibleInputTarget.checked : undefined,
        per_entry: this.hasPerEntryInputTarget ? this.perEntryInputTarget.checked : undefined,
        check_in_changeable: this.hasCheckInChangeableInputTarget
          ? this.checkInChangeableInputTarget.checked
          : undefined,
      },
    }

    // Add options if we have them
    if (this.hasOptionsListTarget) {
      const options = this.collectOptions()
      if (options.length > 0) {
        data.timesheet_template_field.settings = { options: options }
      }
    }

    // Option source and selection bounds — sent with every save, since the
    // controller rebuilds settings from scratch and would drop them otherwise.
    if (this.hasOptionsSourceTarget) {
      const settings = (data.timesheet_template_field.settings =
        data.timesheet_template_field.settings || {})
      settings.options_source = this.selectedOptionsSource()
      if (settings.options_source === "employees") {
        settings.employee_type_ids = this.hasEmployeeTypeIdsTarget
          ? Array.from(this.employeeTypeIdsTarget.selectedOptions).map((option) => option.value)
          : []
        settings.exclude_self = this.hasExcludeSelfInputTarget
          ? this.excludeSelfInputTarget.checked
          : true
      }
    }
    if (this.hasMinSelectionsInputTarget || this.hasMaxSelectionsInputTarget) {
      const settings = (data.timesheet_template_field.settings =
        data.timesheet_template_field.settings || {})
      if (this.hasMinSelectionsInputTarget) {
        settings.min_selections = this.minSelectionsInputTarget.value
      }
      if (this.hasMaxSelectionsInputTarget) {
        settings.max_selections = this.maxSelectionsInputTarget.value
      }
    }

    // Upload field settings — sent whenever the section is rendered, since the
    // controller rebuilds settings from scratch and would drop them otherwise.
    if (this.hasDocumentSyncEnabledInputTarget) {
      data.timesheet_template_field.settings = data.timesheet_template_field.settings || {}
      if (this.hasUploadMultipleInputTarget) {
        data.timesheet_template_field.settings.multiple = this.uploadMultipleInputTarget.checked
      }
      data.timesheet_template_field.settings.document_sync = {
        enabled: this.documentSyncEnabledInputTarget.checked,
        folder_path: this.hasDocumentSyncFolderInputTarget
          ? this.documentSyncFolderInputTarget.value
          : "",
        create_folder: this.hasDocumentSyncCreateFolderInputTarget
          ? this.documentSyncCreateFolderInputTarget.checked
          : true,
      }
    }

    // Text fields: single or multi-line. Sent with every save, since the controller
    // rebuilds settings from scratch and would fall back to single-line otherwise.
    const checkedInputStyle = this.inputStyleTargets.find((input) => input.checked)
    if (checkedInputStyle) {
      data.timesheet_template_field.settings = data.timesheet_template_field.settings || {}
      data.timesheet_template_field.settings.input_style = checkedInputStyle.value
    }

    // Groups: never / open / closed ("" keeps the group always open).
    const checkedCollapse = this.groupCollapseTargets.find((input) => input.checked)
    if (checkedCollapse) {
      data.timesheet_template_field.settings = data.timesheet_template_field.settings || {}
      data.timesheet_template_field.settings.collapse = checkedCollapse.value
    }

    // Conditional visibility (fields + groups): always send when the editor is
    // present so clearing every rule persists (the controller rebuilds settings).
    if (this.hasConditionsListTarget || this.hasConditionsMatchTarget) {
      data.timesheet_template_field.settings = data.timesheet_template_field.settings || {}
      const conditions = this.collectConditions()
      data.timesheet_template_field.settings.conditions = conditions.rules
      data.timesheet_template_field.settings.conditions_match = conditions.match
    }

    // Remove undefined values
    Object.keys(data.timesheet_template_field).forEach((key) => {
      if (data.timesheet_template_field[key] === undefined) {
        delete data.timesheet_template_field[key]
      }
    })

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error("Update failed")
        }
      })
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        console.error("Error updating timesheet field:", error)
        alert(this.errorMessageValue)
      })
  }

  // Save timesheet template allowance via fetch
  saveTimesheetAllowance(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = this.formTarget.dataset.updateUrl
    if (!url) {
      console.error("No update URL found")
      return
    }

    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    // Get allowance type (new) or formula type (legacy)
    const allowanceType = this.hasAllowanceTypeTarget ? this.allowanceTypeTarget.value : null
    const formulaType = this.hasFormulaTypeTarget ? this.formulaTypeTarget.value : null

    // Build the data object
    const data = {
      timesheet_template_allowance: {
        name: this.hasNameInputTarget ? this.nameInputTarget.value : undefined,
        // Blank falls back to the template name (normalized to nil server-side).
        display_name: this.hasDisplayNameInputTarget
          ? this.displayNameInputTarget.value
          : undefined,
        show_formula_hint: this.hasShowFormulaHintInputTarget
          ? this.showFormulaHintInputTarget.checked
          : undefined,
        shown_to_employee: this.hasShownToEmployeeInputTarget
          ? this.shownToEmployeeInputTarget.checked
          : undefined,
        // Blank = free input. The server drops it for non-hours input types.
        input_step_minutes: this.hasInputStepMinutesInputTarget
          ? this.inputStepMinutesInputTarget.value || null
          : undefined,
        // Use allowance_type if available, otherwise fall back to legacy fields
        allowance_type: allowanceType || undefined,
        input_type:
          !allowanceType && this.hasInputTypeTarget ? this.inputTypeTarget.value : undefined,
        formula_type: !allowanceType && formulaType ? formulaType : undefined,
        active: this.hasActiveInputTarget ? this.allowanceActive() : undefined,
        whole_day_option: this.hasWholeDayInputTarget
          ? this.wholeDayInputTarget.checked
          : undefined,
        per_entry: this.hasPerEntryInputTarget ? this.perEntryInputTarget.checked : undefined,
        skip_option: this.hasSkipOptionInputTarget ? this.skipOptionInputTarget.checked : undefined,
        once_per_day: this.hasOncePerDayInputTarget
          ? this.oncePerDayInputTarget.checked
          : undefined,
        once_per_day_winner: this.hasOncePerDayWinnerInputTarget
          ? this.oncePerDayWinnerInputTarget.value
          : undefined,
        once_per_day_strict: this.hasOncePerDayStrictInputTarget
          ? this.oncePerDayStrictInputTarget.checked
          : undefined,
        once_per_week: this.hasOncePerWeekInputTarget
          ? this.oncePerWeekInputTarget.checked
          : undefined,
        // Blank clears the cap (null), so the field can be emptied back to unlimited.
        max_per_week: this.hasMaxPerWeekInputTarget
          ? this.maxPerWeekInputTarget.value || null
          : undefined,
        // Blank clears the Betragsmaximum (null) — empty means unlimited.
        max_amount: this.hasMaxAmountInputTarget
          ? this.maxAmountInputTarget.value || null
          : undefined,
        max_amount_scope: this.hasMaxAmountScopeInputTarget
          ? this.maxAmountScopeInputTarget.value
          : undefined,
        // Blank clears the Stundenmaximum (null) — empty means unlimited. A hidden
        // section clears it too: the cap is only valid on the hour-based formula
        // types, so switching to another type would otherwise leave a stale value
        // behind that the model then refuses to save.
        max_hours: this.hasMaxHoursInputTarget
          ? this.hoursCapSectionVisible()
            ? this.maxHoursInputTarget.value || null
            : null
          : undefined,
        max_hours_scope: this.hasMaxHoursScopeInputTarget
          ? this.maxHoursScopeInputTarget.value
          : undefined,
        threshold_scope: this.hasThresholdScopeInputTarget
          ? this.thresholdScopeInputTarget.value
          : undefined,
        auto_calculated: this.hasAutoCalculatedInputTarget
          ? this.autoCalculatedInputTarget.checked
          : undefined,
        salary_type_number_id: this.hasSalaryTypeNumberInputTarget
          ? this.salaryTypeNumberInputTarget.value || null
          : undefined,
        secondary_salary_type_number_id: this.hasSecondarySalaryTypeNumberInputTarget
          ? this.secondarySalaryTypeNumberInputTarget.value || null
          : undefined,
        tier_salary_type_number_id: this.hasTierSalaryTypeNumberInputTarget
          ? this.tierSalaryTypeNumberInputTarget.value || null
          : undefined,
      },
    }

    // Add formula_config based on allowance_type or formula_type
    const configKey = allowanceType || formulaType
    if (configKey) {
      data.timesheet_template_allowance.formula_config = this.collectFormulaConfig(configKey)
    }

    // Ausgenommene Mitarbeiter: always sent so emptying the select clears the list.
    if (this.hasExcludedEmployeeIdsInputTarget) {
      data.timesheet_template_allowance.excluded_employee_ids = Array.from(
        this.excludedEmployeeIdsInputTarget.selectedOptions,
      ).map((option) => option.value)
    }

    // Gate (per-entry custom-field gate): always send so the admin can clear it.
    // gate_allowed_values is filtered to the currently picked field so stale
    // checkboxes in hidden groups don't leak into the payload.
    if (this.hasGateFieldKeyInputTarget) {
      const gateFieldKey = this.gateFieldKeyInputTarget.value || ""
      data.timesheet_template_allowance.gate_field_key = gateFieldKey
      data.timesheet_template_allowance.gate_allowed_values = gateFieldKey
        ? this.gateAllowedValuesInputTargets
            .filter((input) => input.dataset.gateFieldKey === gateFieldKey && input.checked)
            .map((input) => input.value)
        : []
    }

    // Export-Darstellung: the one setting every export format reads. Always sent so
    // the admin can switch back to "auto".
    if (this.hasExportValueModeInputTarget) {
      data.timesheet_template_allowance.export_value_mode =
        this.exportValueModeInputTarget.value || "auto"
    }

    // Per-format export config: the editor owns ONLY the skip flag. The value sources
    // are a console-only expert seam, so nothing else is sent and the server merges
    // rather than rebuilding — sending an empty value_source here would wipe a source
    // set via the console the first time anyone saves the allowance.
    // skip is sent as a string to match the controller's `== 'true'` parsing.
    if (this.hasAddisonSkipExportInputTarget) {
      data.timesheet_template_allowance.addison_export_config = {
        skip: this.addisonSkipExportInputTarget.checked ? "true" : "false",
      }
    }
    if (this.hasGdiSkipExportInputTarget) {
      data.timesheet_template_allowance.gdi_export_config = {
        skip: this.gdiSkipExportInputTarget.checked ? "true" : "false",
      }
    }

    // Remove undefined values
    Object.keys(data.timesheet_template_allowance).forEach((key) => {
      if (data.timesheet_template_allowance[key] === undefined) {
        delete data.timesheet_template_allowance[key]
      }
    })

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error("Update failed")
        }
      })
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        console.error("Error updating timesheet allowance:", error)
        this.showAllowanceError(this.errorMessageValue)
      })
  }

  // The Aktiv flag belongs to the switch on the collapsed card; the editor only
  // carries its current value in a hidden field so the save can echo it back.
  // Reading `.checked` off a hidden input is always false — that silently
  // deactivated the allowance on every save.
  allowanceActive() {
    const input = this.activeInputTarget
    return input.type === "checkbox" ? input.checked : input.value === "true"
  }

  // A window.alert blocks every following browser event, which is fatal in a
  // card the user is still editing — the message goes into the editor's own
  // banner instead.
  showAllowanceError(message) {
    if (!this.hasAllowanceErrorBannerTarget || !this.hasAllowanceErrorTextTarget) {
      alert(message)
      return
    }
    // The message goes into the banner's <p>, not the banner itself — writing
    // textContent on the container replaces that styled child with a bare text
    // node, so the first failure renders unstyled and the second has no <p> left.
    this.allowanceErrorTextTarget.textContent = message
    this.allowanceErrorBannerTarget.classList.remove("hidden")
  }

  // Select a preset PDF color
  selectPdfColor(event) {
    const colorValue = event.currentTarget.dataset.colorValue
    if (this.hasPdfColorTarget) {
      this.pdfColorTarget.value = colorValue
    }
    // Update visual selection: remove ring from all swatches, add to selected
    this.element
      .querySelectorAll('[data-action="click->field-editor#selectPdfColor"]')
      .forEach((btn) => {
        btn.classList.remove("border-gray-800", "ring-2", "ring-offset-1", "ring-gray-400")
        btn.classList.add("border-gray-300")
      })
    event.currentTarget.classList.remove("border-gray-300")
    event.currentTarget.classList.add("border-gray-800", "ring-2", "ring-offset-1", "ring-gray-400")
    // Remove ring from custom color button
    const customBtn = this.element.querySelector(
      '[data-action="click->field-editor#selectCustomPdfColor"]',
    )
    if (customBtn) {
      customBtn.classList.remove("border-gray-800", "ring-2", "ring-offset-1", "ring-gray-400")
      customBtn.classList.add("border-gray-300")
    }
  }

  // Open the custom color picker
  selectCustomPdfColor(_event) {
    if (this.hasPdfColorPickerTarget) {
      this.pdfColorPickerTarget.click()
    }
  }

  // Handle custom color picker change
  onCustomPdfColorChange(event) {
    const hexValue = event.currentTarget.value
    if (this.hasPdfColorTarget) {
      this.pdfColorTarget.value = hexValue
    }
    // Update visual selection: remove ring from presets, add to custom
    this.element
      .querySelectorAll('[data-action="click->field-editor#selectPdfColor"]')
      .forEach((btn) => {
        btn.classList.remove("border-gray-800", "ring-2", "ring-offset-1", "ring-gray-400")
        btn.classList.add("border-gray-300")
      })
    const customBtn = this.element.querySelector(
      '[data-action="click->field-editor#selectCustomPdfColor"]',
    )
    if (customBtn) {
      customBtn.classList.remove("border-gray-300")
      customBtn.classList.add("border-gray-800", "ring-2", "ring-offset-1", "ring-gray-400")
      customBtn.style.backgroundColor = hexValue
    }
  }

  // Update field visibility based on allowance type selection
  updateAllowanceTypeFields() {
    if (!this.hasAllowanceTypeTarget) return

    const allowanceType = this.allowanceTypeTarget.value
    const automaticTypes = [
      "nachtzulage",
      "sonntagszulage",
      "feiertagszulage",
      "kilometergeldzulage",
      "unterkunft_baustelle",
    ]
    const isAutomatic = automaticTypes.includes(allowanceType)
    // Types that can be flipped into auto-calculate mode (no employee input):
    // the daily flat rate, the per-entry flat rate, custom Dentaku formulas and
    // the weekly Fahrgeld (whose auto grant is driven by the custom-field gate).
    const autoCalcCapableTypes = [
      "tagespauschale",
      "eintragspauschale",
      "benutzerdefiniert",
      "wochentliches_fahrgeld",
    ]
    const autoCalcChecked =
      this.hasAutoCalculatedInputTarget && this.autoCalculatedInputTarget.checked
    const autoCalcActive =
      isAutomatic || (autoCalcCapableTypes.includes(allowanceType) && autoCalcChecked)

    // Show/hide formula config sections based on allowance type.
    // data-allowance-type-alt may carry multiple space-separated alternates
    // so a single config block can be shared by 3+ sibling types
    // (e.g. kilometergeldzulage / baustelle_baustelle_zulage / unterkunft_baustelle).
    this.element.querySelectorAll("[data-allowance-type]").forEach((el) => {
      const elType = el.dataset.allowanceType
      const altTypes = (el.dataset.allowanceTypeAlt || "").split(/\s+/).filter(Boolean)
      const shouldShow = elType === allowanceType || altTypes.includes(allowanceType)
      el.classList.toggle("hidden", !shouldShow)
    })

    // Show/hide manual-only sections (currently just the whole day option —
    // the intrinsic-automatic types do not allow whole-day toggling).
    this.element.querySelectorAll("[data-manual-only]").forEach((el) => {
      el.classList.toggle("hidden", isAutomatic)
    })

    // The Steuer-Split section itself is always rendered. Only its inner
    // subgroups (opt-in toggle row, tenure-fields, details block) change per
    // allowance type, driven by lists embedded on the section element.
    this.element.querySelectorAll("[data-tax-split-section]").forEach((section) => {
      const tenureAlwaysTypes = (section.dataset.tenureAlwaysTypes || "").split(",").filter(Boolean)
      const tenureOptInTypes = (section.dataset.tenureOptInTypes || "").split(",").filter(Boolean)

      const showToggle = tenureOptInTypes.includes(allowanceType)
      section.querySelectorAll("[data-tax-split-toggle-row]").forEach((el) => {
        el.classList.toggle("hidden", !showToggle)
      })

      const showTenureFields =
        tenureAlwaysTypes.includes(allowanceType) || tenureOptInTypes.includes(allowanceType)
      section.querySelectorAll("[data-tax-split-tenure-fields]").forEach((el) => {
        el.classList.toggle("hidden", !showTenureFields)
      })

      // Details subgroup: always visible except for opt-in types where the
      // enable checkbox gates it.
      const toggleOn =
        !showToggle ||
        (this.hasTenureSplitEnabledInputTarget && this.tenureSplitEnabledInputTarget.checked)
      section.querySelectorAll("[data-tax-split-details]").forEach((el) => {
        el.classList.toggle("hidden", !toggleOn)
      })
    })

    // Staffelung nach Stunden: only the three per-hour formula types can stagger a
    // rate, so the whole section is hidden for anything else. The list of eligible
    // types is embedded on the section rather than duplicated here.
    this.element.querySelectorAll("[data-rate-tier-section]").forEach((section) => {
      const rateTierTypes = (section.dataset.rateTierTypes || "").split(",").filter(Boolean)
      section.classList.toggle("hidden", !rateTierTypes.includes(allowanceType))

      const enabled = this.hasRateTierEnabledInputTarget && this.rateTierEnabledInputTarget.checked
      section.querySelectorAll("[data-rate-tier-details]").forEach((el) => {
        el.classList.toggle("hidden", !enabled)
      })
    })

    // Stundenmaximum: only the formula types whose payout follows the hours can cap
    // them. Same embedded-list approach as the rate tier above.
    this.element.querySelectorAll("[data-hours-cap-section]").forEach((section) => {
      const hoursCapTypes = (section.dataset.hoursCapTypes || "").split(",").filter(Boolean)
      section.classList.toggle("hidden", !hoursCapTypes.includes(allowanceType))
    })

    // Show/hide the auto-calculate toggle itself (daily flat rate, per-entry flat
    // rate, custom formulas). Driven by a dedicated marker because the generic
    // data-allowance-type toggle above only supports two types.
    this.element.querySelectorAll("[data-auto-calc-section]").forEach((el) => {
      el.classList.toggle("hidden", !autoCalcCapableTypes.includes(allowanceType))
    })

    // Show/hide skip option sections (for auto-calculated allowances, incl.
    // custom/daily-flat-rate allowances flagged auto-calculate)
    this.element.querySelectorAll("[data-auto-calculated-only]").forEach((el) => {
      el.classList.toggle("hidden", !autoCalcActive)
    })
  }

  // Show the "which entry gets it" picker only while "Einmal pro Tag" is ticked —
  // without the dedup there is nothing to elect.
  toggleOncePerDayWinner() {
    if (!this.hasOncePerDayInputTarget) return

    const active = this.oncePerDayInputTarget.checked
    this.element.querySelectorAll("[data-once-per-day-only]").forEach((el) => {
      el.classList.toggle("hidden", !active)
    })
  }

  // Hide/show the Steuer-Split details subgroup (threshold + reset-gap +
  // secondary Lohnart) when the opt-in tenure toggle flips. Event-based and
  // meal-allowance splits don't render the toggle, so this is a no-op for them.
  // Whether the Stundenmaximum section is offered for the currently selected
  // allowance type. A hidden section must post a cleared cap rather than the value
  // its input still holds — see the max_hours payload above.
  hoursCapSectionVisible() {
    const section = this.element.querySelector("[data-hours-cap-section]")
    return !section || !section.classList.contains("hidden")
  }

  // Show or hide the rate-tier fields when the "Staffelung nach Stunden" checkbox
  // flips. The server clears the config when the box is unticked, so hiding alone
  // never leaves a half-applied tier behind.
  toggleRateTierDetails() {
    if (!this.hasRateTierEnabledInputTarget) return

    const enabled = this.rateTierEnabledInputTarget.checked
    this.element.querySelectorAll("[data-rate-tier-details]").forEach((el) => {
      el.classList.toggle("hidden", !enabled)
    })
  }

  // Allowance types that offer the opt-in Steuer-Split, as embedded by the server.
  // Without a section (no Lohnarten configured) there is no toggle to read either.
  tenureOptInAllowanceTypes() {
    const section = this.element.querySelector("[data-tax-split-section]")
    return (section?.dataset.tenureOptInTypes || "").split(",").filter(Boolean)
  }

  toggleTaxSplitDetails() {
    if (!this.hasTenureSplitEnabledInputTarget) return

    const enabled = this.tenureSplitEnabledInputTarget.checked
    this.element.querySelectorAll("[data-tax-split-details]").forEach((el) => {
      el.classList.toggle("hidden", !enabled)
    })
  }

  // Swap the tenure threshold input between "Tage" and "Kalendermonate" when
  // the mode select changes. Only one of the two rows is visible at a time;
  // the server drops the hidden row's value on save.
  toggleTenureThresholdMode() {
    if (!this.hasTenureThresholdModeInputTarget) return

    const monthMode = this.tenureThresholdModeInputTarget.value === "calendar_months"
    this.element.querySelectorAll("[data-tenure-threshold-days-row]").forEach((el) => {
      el.classList.toggle("hidden", monthMode)
    })
    this.element.querySelectorAll("[data-tenure-threshold-months-row]").forEach((el) => {
      el.classList.toggle("hidden", !monthMode)
    })
  }

  // Toggle the gate-allowed-values checkbox group that matches the picked
  // field key; hide every other group. One group per dropdown field on the
  // template, each tagged with data-gate-field-key.
  updateGateAllowedValues() {
    if (!this.hasGateFieldKeyInputTarget) return

    const selected = this.gateFieldKeyInputTarget.value
    this.gateValuesGroupTargets.forEach((el) => {
      el.classList.toggle("hidden", el.dataset.gateFieldKey !== selected)
    })
  }

  collectFormulaConfig(typeKey) {
    const config = {}

    // Map allowance types to formula types for config collection
    const allowanceTypeToFormulaType = {
      nachtzulage: "time_based_split",
      sonntagszulage: "day_of_week_split",
      feiertagszulage: "holiday_split",
      kilometergeldzulage: "mileage_allowance",
      unterkunft_baustelle: "mileage_allowance",
      stundenzulage: "hours_times_rate",
      stundenlohn_multiplikator: "hours_times_multiplier",
      tagespauschale: "fixed_per_day",
      eintragspauschale: "fixed_per_entry",
      prozent_vom_lohn: "percentage_of_wage",
      benutzerdefiniert: "custom",
      entfernungspauschale: "distance_based_flat",
      wochentliches_fahrgeld: "wochentliches_fahrgeld",
      baustelle_baustelle_zulage: "mileage_allowance",
      verpflegungspauschale: "meal_allowance_with_tenure_split",
    }

    // Get the formula type (either directly or mapped from allowance type)
    const formulaType = allowanceTypeToFormulaType[typeKey] || typeKey

    switch (formulaType) {
      case "hours_times_rate":
        if (this.hasRateInputTarget) {
          config.rate = parseFloat(this.rateInputTarget.value) || 0
        }
        this.collectRateTier(config)
        break
      case "hours_times_multiplier":
        if (this.hasMultiplierInputTarget) {
          config.multiplier = parseFloat(this.multiplierInputTarget.value) || 1
        }
        this.collectRateTier(config)
        break
      case "fixed_per_day":
      case "fixed_per_entry":
        if (this.hasFixedAmountInputTarget) {
          config.fixed_amount = parseFloat(this.fixedAmountInputTarget.value) || 0
        }
        this.collectTimeWindow(config)
        break
      case "percentage_of_wage":
        if (this.hasPercentageInputTarget) {
          config.percentage = parseFloat(this.percentageInputTarget.value) || 0
        }
        this.collectRateTier(config)
        break
      case "custom":
        if (this.hasExpressionInputTarget) {
          config.expression = this.expressionInputTarget.value
        }
        break
      case "time_based_split":
        if (this.hasReferenceTimeFromInputTarget) {
          config.reference_time_from = this.referenceTimeFromInputTarget.value
        }
        if (this.hasReferenceTimeToInputTarget) {
          config.reference_time_to = this.referenceTimeToInputTarget.value
        }
        if (this.hasSeasonalConfigsContainerTarget) {
          const seasonalConfigs = []
          const seasonalRows = this.seasonalConfigsContainerTarget.querySelectorAll(".seasonal-row")
          seasonalRows.forEach((row) => {
            seasonalConfigs.push({
              from_month:
                parseInt(row.querySelector('[data-seasonal-field="from_month"]')?.value) || 0,
              from_day: parseInt(row.querySelector('[data-seasonal-field="from_day"]')?.value) || 0,
              to_month: parseInt(row.querySelector('[data-seasonal-field="to_month"]')?.value) || 0,
              to_day: parseInt(row.querySelector('[data-seasonal-field="to_day"]')?.value) || 0,
              reference_time_from:
                row.querySelector('[data-seasonal-field="reference_time_from"]')?.value || "",
              reference_time_to:
                row.querySelector('[data-seasonal-field="reference_time_to"]')?.value || "",
            })
          })
          config.seasonal_configs = seasonalConfigs
        }
        break
      case "day_of_week_split":
        if (this.hasTriggerDayInputTarget) {
          config.trigger_days = this.triggerDayInputTargets
            .filter((input) => input.checked)
            .map((input) => parseInt(input.dataset.dayIndex))
        }
        // Blank = off: omitted so the controller drops the key.
        if (this.hasAfterDailyHoursInputTarget && this.afterDailyHoursInputTarget.value) {
          config.after_daily_hours = parseFloat(this.afterDailyHoursInputTarget.value)
        }
        this.collectTimeWindow(config)
        break
      case "holiday_split":
        // Holidays are detected automatically for the configured region source.
        if (this.hasHolidayRegionSourceInputTarget) {
          config.holiday_region_source = this.holidayRegionSourceInputTarget.value || "tenant"
        }
        this.collectTimeWindow(config)
        break
      case "mileage_allowance":
        // Tag the basis so the calculator picks the right distance: the new
        // Baustelle-Baustelle type reads the per-day site-to-site total, the
        // legacy Kilometergeldzulage reads the home→site commute distance.
        // A Kilometergeldzulage may instead measure home → the employee's Standort.
        if (typeKey === "baustelle_baustelle_zulage") {
          config.mileage_distance_basis = "site_to_site"
        } else if (
          typeKey === "kilometergeldzulage" &&
          this.checkedMileageDistanceBasis() === "home_to_location"
        ) {
          config.mileage_distance_basis = "home_to_location"
        } else {
          config.mileage_distance_basis = "home_to_site"
        }
        if (this.hasMileageDistanceSourceInputTarget) {
          const checkedSource = this.mileageDistanceSourceInputTargets.find(
            (input) => input.checked,
          )
          config.distance_source = checkedSource?.value || "automatic"
        }
        if (this.hasMileageDistanceMethodInputTarget) {
          const checkedMethod = this.mileageDistanceMethodInputTargets.find(
            (input) => input.checked,
          )
          config.distance_method = checkedMethod?.value || "driving"
        }
        if (this.hasMileageReferencePointInputTarget) {
          const checkedReference = this.mileageReferencePointInputTargets.find(
            (input) => input.checked,
          )
          config.reference_point = checkedReference?.value || "employee_home"
        }
        if (this.hasMileageRoundDistanceInputTarget) {
          config.round_distance_to_half_km = this.mileageRoundDistanceInputTarget.checked
        }
        if (this.hasMileageRoundTripInputTarget) {
          config.round_trip = this.mileageRoundTripInputTarget.checked
        }
        if (this.hasMileageCapToHighestRangeInputTarget) {
          config.mileage_cap_to_highest_range = this.mileageCapToHighestRangeInputTarget.checked
        }
        if (this.hasMileageCalculationModeInputTarget) {
          const checkedMode = this.mileageCalculationModeInputTargets.find((input) => input.checked)
          config.mileage_calculation_mode = checkedMode?.value || "fixed_per_range"
        }
        if (this.hasMileageRangesContainerTarget) {
          const ranges = []
          const rangeRows = this.mileageRangesContainerTarget.querySelectorAll(".range-row")
          rangeRows.forEach((row) => {
            const fromInput = row.querySelector('[data-range-field="from"]')
            const toInput = row.querySelector('[data-range-field="to"]')
            const fixedAmountInput = row.querySelector('[data-range-field="fixed_amount"]')
            const perKmRateInput = row.querySelector('[data-range-field="per_km_rate"]')

            ranges.push({
              from: parseFloat(fromInput?.value) || 0,
              to: toInput?.value ? parseFloat(toInput.value) : null,
              fixed_amount: parseFloat(fixedAmountInput?.value) || 0,
              per_km_rate: parseFloat(perKmRateInput?.value) || 0,
            })
          })
          config.mileage_ranges = ranges
        }
        break
      case "distance_based_flat":
        if (this.hasDistanceFixedAmountInputTarget) {
          config.fixed_amount = parseFloat(this.distanceFixedAmountInputTarget.value) || 0
        }
        if (this.hasDistanceThresholdInputTarget) {
          config.distance_threshold_km = parseFloat(this.distanceThresholdInputTarget.value) || 0
        }
        if (this.hasDistanceMethodInputTarget) {
          const checkedMethod = this.distanceMethodInputTargets.find((input) => input.checked)
          config.distance_method = checkedMethod?.value || "air_distance"
        }
        if (this.hasDistanceOriginInputTarget) {
          const checkedOrigin = this.distanceOriginInputTargets.find((input) => input.checked)
          config.distance_origin = checkedOrigin?.value || "company_hq"
        }
        if (this.hasOriginAddressInputTarget) {
          config.origin_address = this.originAddressInputTarget.value
        }
        if (this.hasOriginLatitudeInputTarget && this.originLatitudeInputTarget.value) {
          config.origin_latitude = this.originLatitudeInputTarget.value
        }
        if (this.hasOriginLongitudeInputTarget && this.originLongitudeInputTarget.value) {
          config.origin_longitude = this.originLongitudeInputTarget.value
        }
        if (this.hasExcludedPostalCodesInputTarget) {
          config.excluded_postal_codes = this.excludedPostalCodesInputTarget.value
        }
        break
      case "wochentliches_fahrgeld":
        if (this.hasWochentlichesFahrgeldRateInputTarget) {
          config.per_km_rate = parseFloat(this.wochentlichesFahrgeldRateInputTarget.value) || 0
        }
        break
    }

    // Tenure-based tax split — meal allowance always carries the keys; the
    // opt-in allowance types carry them when the enable toggle is on. The
    // controller mirrors this by clearing the keys when tenure_split_enabled
    // arrives as false. The opt-in list comes from the server
    // (TENURE_OPT_IN_ALLOWANCE_TYPES on the Steuer-Split section): a copy kept
    // here once missed the mileage types, so their split was switched off on
    // every save.
    const isMealTenureSplit = formulaType === "meal_allowance_with_tenure_split"
    const isOptInTenureCapable = this.tenureOptInAllowanceTypes().includes(typeKey)

    if (isMealTenureSplit) {
      // Preserve non-tenure meal config (currently set via the model defaults
      // and round-tripped through a hidden input) so editing the threshold
      // doesn't wipe fixed_amount. minimum_hours is captured by the universal
      // block below.
      if (this.hasMealFixedAmountInputTarget && this.mealFixedAmountInputTarget.value) {
        config.fixed_amount = parseFloat(this.mealFixedAmountInputTarget.value)
      }
      this.collectTenureThreshold(config)
      if (
        this.hasTenureResetAfterGapDaysInputTarget &&
        this.tenureResetAfterGapDaysInputTarget.value
      ) {
        config.tenure_reset_after_gap_days = parseInt(
          this.tenureResetAfterGapDaysInputTarget.value,
          10,
        )
      }
    } else if (isOptInTenureCapable) {
      const enabled =
        this.hasTenureSplitEnabledInputTarget && this.tenureSplitEnabledInputTarget.checked
      config.tenure_split_enabled = enabled
      if (enabled) {
        this.collectTenureThreshold(config)
      }
      if (
        enabled &&
        this.hasTenureResetAfterGapDaysInputTarget &&
        this.tenureResetAfterGapDaysInputTarget.value
      ) {
        config.tenure_reset_after_gap_days = parseInt(
          this.tenureResetAfterGapDaysInputTarget.value,
          10,
        )
      }
    }

    // Universal minimum_hours threshold (works for every allowance type).
    // Blank/zero disables the gate; the back end stores the parsed float in
    // formula_config['minimum_hours'] and pairs it with threshold_scope.
    if (this.hasMinimumHoursInputTarget) {
      const raw = this.minimumHoursInputTarget.value
      if (raw !== "" && raw !== null && raw !== undefined) {
        const parsed = parseFloat(raw)
        if (!Number.isNaN(parsed)) {
          config.minimum_hours = parsed
        }
      }
    }

    // "Gültig bis": the date picker's hidden field carries the ISO date; blank = no end.
    if (this.hasValidUntilInputTarget && this.validUntilInputTarget.value) {
      config.valid_until = this.validUntilInputTarget.value
    }

    this.collectMutualExclusion(config)
    this.collectExcludedSites(config)
    this.collectLocationVerification(config)

    return config
  }

  // Serialize the two opt-in location gates. Booleans are always sent (the back end
  // writes them explicitly so unchecking clears the gate); the radius / no-GPS
  // sub-settings are only sent while the toggle is on.
  collectLocationVerification(config) {
    if (this.hasSuppressStartAtHqInputTarget) {
      const on = this.suppressStartAtHqInputTarget.checked
      config.suppress_when_start_at_hq = on
      if (on) {
        if (this.hasHqGeofenceMetersInputTarget) {
          const parsed = parseInt(this.hqGeofenceMetersInputTarget.value, 10)
          if (!Number.isNaN(parsed)) config.hq_geofence_meters = parsed
        }
        if (this.hasHqStartNoGpsInputTarget) {
          config.hq_start_no_gps = this.hqStartNoGpsInputTarget.value
        }
      }
    }

    if (this.hasRequireSitePresenceInputTarget) {
      const on = this.requireSitePresenceInputTarget.checked
      config.require_site_presence = on
      if (on && this.hasSitePresenceNoGpsInputTarget) {
        config.site_presence_no_gps = this.sitePresenceNoGpsInputTarget.value
      }
      // Sent even when blank: the back end reads an empty string as "fall back to the
      // tenant default" and deletes the key, which is how a threshold gets cleared.
      if (on && this.hasSitePresenceMinDwellInputTarget) {
        config.site_presence_min_dwell_minutes = this.sitePresenceMinDwellInputTarget.value
      }
      if (on && this.hasSitePresenceMinPingsInputTarget) {
        config.site_presence_min_pings = this.sitePresenceMinPingsInputTarget.value
      }
      if (on && this.hasSitePresenceMinShareInputTarget) {
        config.site_presence_min_share_percent = this.sitePresenceMinShareInputTarget.value
      }
    }
  }

  // Serialize the per-allowance site blacklist: the selected site ids. An empty
  // selection omits the key so the back end clears the blacklist.
  collectExcludedSites(config) {
    if (!this.hasExcludedSiteIdsInputTarget) return

    const ids = Array.from(this.excludedSiteIdsInputTarget.selectedOptions).map(
      (option) => option.value,
    )
    if (ids.length > 0) {
      config.excluded_site_ids = ids
    }
  }

  // Serialize cross-scope mutual exclusion: the selected sibling allowance keys,
  // the scope they are evaluated at (entry/day/week), and the week grouping. An
  // empty selection omits the keys so the back end leaves the gate disabled.
  collectMutualExclusion(config) {
    if (this.hasMutuallyExclusiveInputTarget) {
      const keys = Array.from(this.mutuallyExclusiveInputTarget.selectedOptions).map(
        (option) => option.value,
      )
      if (keys.length > 0) {
        config.mutually_exclusive_with = keys
      }
    }
    if (this.hasExclusionScopeInputTarget && this.exclusionScopeInputTarget.value) {
      config.exclusion_scope = this.exclusionScopeInputTarget.value
    }
    if (this.hasExclusionWeekGroupingInputTarget && this.exclusionWeekGroupingInputTarget.value) {
      config.exclusion_week_grouping = this.exclusionWeekGroupingInputTarget.value
    }
  }

  // Serialize the tenure threshold: the mode plus the threshold field matching
  // it. The server drops the other mode's value, so only the visible input is
  // sent with a value.
  collectTenureThreshold(config) {
    const monthMode =
      this.hasTenureThresholdModeInputTarget &&
      this.tenureThresholdModeInputTarget.value === "calendar_months"
    config.tenure_threshold_mode = monthMode ? "calendar_months" : "days"

    if (monthMode) {
      if (this.hasTenureThresholdMonthsInputTarget && this.tenureThresholdMonthsInputTarget.value) {
        config.tenure_threshold_months = parseInt(this.tenureThresholdMonthsInputTarget.value, 10)
      }
    } else if (
      this.hasTenureThresholdDaysInputTarget &&
      this.tenureThresholdDaysInputTarget.value
    ) {
      config.tenure_threshold_days = parseInt(this.tenureThresholdDaysInputTarget.value, 10)
    }
  }

  // Serialize the "Staffelung nach Stunden" config. Always sent for the eligible
  // formula types, including the disabled state, so unticking the box clears the
  // stored tier server-side instead of leaving it behind.
  collectRateTier(config) {
    if (!this.hasRateTierEnabledInputTarget) return

    if (!this.rateTierEnabledInputTarget.checked) {
      config.rate_tier_enabled = false
      return
    }

    config.rate_tier_enabled = true
    if (this.hasRateTierScopeInputTarget) {
      config.rate_tier_scope = this.rateTierScopeInputTarget.value
    }
    if (this.hasRateTierThresholdInputTarget) {
      config.rate_tier_threshold_hours = parseFloat(this.rateTierThresholdInputTarget.value) || 0
    }
    if (this.hasRateTierValueInputTarget) {
      config.rate_tier_value = parseFloat(this.rateTierValueInputTarget.value) || 0
    }
  }

  // The Kilometergeld "Strecke" radio: "home_to_site" (default) or "home_to_location".
  checkedMileageDistanceBasis() {
    if (!this.hasMileageDistanceBasisInputTarget) return "home_to_site"

    const checked = this.mileageDistanceBasisInputTargets.find((input) => input.checked)
    return checked?.value || "home_to_site"
  }

  // Serialize the optional daily time-window bounds. Blank bounds are omitted so
  // the controller clears them (= full day).
  collectTimeWindow(config) {
    if (this.hasTimeWindowFromInputTarget && this.timeWindowFromInputTarget.value) {
      config.time_window_from = this.timeWindowFromInputTarget.value
    }
    if (this.hasTimeWindowToInputTarget && this.timeWindowToInputTarget.value) {
      config.time_window_to = this.timeWindowToInputTarget.value
    }
  }

  // Show the custom origin address field only when the custom origin is selected.
  toggleCustomOriginField() {
    if (!this.hasDistanceOriginInputTarget) return

    const selected = this.distanceOriginInputTargets.find((input) => input.checked)?.value
    this.element.querySelectorAll("[data-custom-origin-field]").forEach((el) => {
      el.classList.toggle("hidden", selected !== "custom")
    })
  }

  // Show the week-grouping select only when the exclusion scope is "week".
  toggleExclusionWeekGrouping() {
    if (!this.hasExclusionScopeInputTarget) return

    const isWeek = this.exclusionScopeInputTarget.value === "week"
    this.element.querySelectorAll("[data-exclusion-week-field]").forEach((el) => {
      el.classList.toggle("hidden", !isWeek)
    })
  }

  // Show the HQ-departure sub-settings only when the check is enabled.
  toggleHqCheckConfig() {
    if (!this.hasSuppressStartAtHqInputTarget) return

    const on = this.suppressStartAtHqInputTarget.checked
    this.element.querySelectorAll("[data-hq-check-field]").forEach((el) => {
      el.classList.toggle("hidden", !on)
    })
  }

  // Show the on-site-presence sub-settings only when the check is enabled.
  toggleSitePresenceConfig() {
    if (!this.hasRequireSitePresenceInputTarget) return

    const on = this.requireSitePresenceInputTarget.checked
    this.element.querySelectorAll("[data-site-presence-field]").forEach((el) => {
      el.classList.toggle("hidden", !on)
    })
  }
}
